import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyReservations, cancelReservation } from '../services/reservationService';
import { initiatePayment } from '../services/paymentService';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import EditReservationModal from '../components/EditReservationModal';
import './MyReservations.css';

const MyReservations = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('upcoming');
  const [editingReservation, setEditingReservation] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(null);
  const { isLoggedIn, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    fetchReservations();
  }, [isLoggedIn, authLoading, navigate]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const response = await getMyReservations();
      let reservationsList = [];
      if (Array.isArray(response)) {
        reservationsList = response;
      } else if (response && Array.isArray(response.data)) {
        reservationsList = response.data;
      } else if (response && Array.isArray(response.results)) {
        reservationsList = response.results;
      }
      setReservations(reservationsList);
      setError('');
    } catch (err) {
      setError('Failed to load your reservations');
      console.error('Error fetching reservations:', err);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReservation = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this reservation?')) return;
    try {
      await cancelReservation(id);
      fetchReservations();
    } catch (err) {
      alert('Failed to cancel reservation. Please try again.');
      console.error(err);
    }
  };

  const handleEditReservation = (reservation) => setEditingReservation(reservation);
  const handleEditSuccess = () => {
    setEditingReservation(null);
    fetchReservations();
  };
  const handleCloseEditModal = () => setEditingReservation(null);

  const handleRetryPayment = async (reservation) => {
    try {
      setPaymentLoading(reservation.id);

      // For per-guest payments, redirect to guest payment page with token
      if (reservation.paymentType === 'per_guest' && reservation.paymentToken) {
        navigate(`/guest-payment/${reservation.paymentToken}`);
        return;
      }

      // For per-reservation payments, use the standard flow
      const response = await initiatePayment(reservation.id);
      if (response.paymentUrl) {
        const params = new URLSearchParams({
          url: response.paymentUrl,
          reservationId: reservation.id.toString(),
          restaurantName: reservation.restaurant?.name || 'Restaurant',
        });
        navigate(`/payment?${params.toString()}`);
      } else {
        alert('Unable to initiate payment. Please try again.');
      }
    } catch (err) {
      console.error('Payment initiation error:', err);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setPaymentLoading(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const isUpcomingReservation = (reservation) => {
    const reservationDate = new Date(reservation.date);
    if (reservation.time) {
      const [hours, minutes] = reservation.time.split(':').map(Number);
      reservationDate.setHours(hours || 0, minutes || 0, 0, 0);
    } else {
      reservationDate.setHours(23, 59, 59, 999);
    }
    return reservationDate >= new Date();
  };

  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#ffffff';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  const canEditReservation = (reservation) => {
    const status = reservation.status?.toLowerCase();
    return isUpcomingReservation(reservation) &&
           status !== 'cancelled' &&
           status !== 'completed' &&
           status !== 'no_show';
  };

  const getFilteredReservations = () => {
    if (!Array.isArray(reservations)) return [];
    const now = new Date();
    const getReservationDateTime = (reservation) => {
      const date = new Date(reservation.date);
      if (reservation.time) {
        const [hours, minutes] = reservation.time.split(':').map(Number);
        date.setHours(hours || 0, minutes || 0, 0, 0);
      } else {
        date.setHours(23, 59, 59, 999);
      }
      return date;
    };
    if (filter === 'upcoming') {
      return reservations.filter(r => getReservationDateTime(r) >= now);
    } else if (filter === 'past') {
      return reservations.filter(r => getReservationDateTime(r) < now);
    }
    return reservations;
  };

  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return { class: 'status-confirmed', icon: 'bi-check-circle-fill', label: 'Confirmed' };
      case 'pending':
        return { class: 'status-pending', icon: 'bi-clock-fill', label: 'Pending' };
      case 'cancelled':
        return { class: 'status-cancelled', icon: 'bi-x-circle-fill', label: 'Cancelled' };
      case 'completed':
        return { class: 'status-completed', icon: 'bi-check-all', label: 'Completed' };
      case 'no_show':
        return { class: 'status-noshow', icon: 'bi-person-x-fill', label: 'No Show' };
      default:
        return { class: 'status-default', icon: 'bi-question-circle', label: status || 'Unknown' };
    }
  };

  const getPaymentConfig = (paymentStatus) => {
    switch (paymentStatus?.toLowerCase()) {
      case 'paid':
        return { class: 'payment-paid', icon: 'bi-check-circle-fill', label: 'Paid' };
      case 'pending':
        return { class: 'payment-pending', icon: 'bi-clock-fill', label: 'Payment Pending' };
      case 'failed':
        return { class: 'payment-failed', icon: 'bi-x-circle-fill', label: 'Payment Failed' };
      default:
        return null;
    }
  };

  const filteredReservations = getFilteredReservations();
  const upcomingCount = reservations.filter(r => isUpcomingReservation(r)).length;
  const pastCount = reservations.filter(r => !isUpcomingReservation(r)).length;

  if (authLoading) {
    return (
      <div className="reservations-page">
        <Navbar />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reservations-page">
      <Navbar />

      <div className="reservations-header">
        <div className="header-content">
          <h1>My Reservations</h1>
          <p>Manage your restaurant bookings</p>
        </div>
      </div>

      <div className="reservations-content">
        <div className="filters-section">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${filter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setFilter('upcoming')}
            >
              <i className="bi bi-calendar-event"></i>
              <span>Upcoming</span>
              {upcomingCount > 0 && <span className="count">{upcomingCount}</span>}
            </button>
            <button
              className={`filter-tab ${filter === 'past' ? 'active' : ''}`}
              onClick={() => setFilter('past')}
            >
              <i className="bi bi-clock-history"></i>
              <span>Past</span>
              {pastCount > 0 && <span className="count">{pastCount}</span>}
            </button>
            <button
              className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              <i className="bi bi-list-ul"></i>
              <span>All</span>
              <span className="count">{reservations.length}</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading reservations...</p>
          </div>
        ) : filteredReservations.length === 0 ? (
          <div className="empty-state-container">
            <div className="empty-state-card">
              <div className="empty-state-illustration">
                <div className="illustration-circle">
                  <div className="illustration-inner">
                    <i className="bi bi-calendar2-heart"></i>
                  </div>
                </div>
                <div className="floating-element elem-1"><i className="bi bi-star-fill"></i></div>
                <div className="floating-element elem-2"><i className="bi bi-heart-fill"></i></div>
                <div className="floating-element elem-3"><i className="bi bi-cup-hot-fill"></i></div>
              </div>
              <div className="empty-state-content">
                <h3>
                  {filter === 'upcoming'
                    ? 'No Upcoming Reservations'
                    : filter === 'past'
                    ? 'No Past Reservations'
                    : 'No Reservations Yet'}
                </h3>
                <p>
                  {filter === 'upcoming'
                    ? "You don't have any upcoming dining experiences scheduled. Discover amazing restaurants and book your next meal!"
                    : filter === 'past'
                    ? "You haven't completed any reservations yet. Your dining history will appear here."
                    : "Start your culinary journey by exploring our curated selection of restaurants and making your first reservation!"}
                </p>
                <button className="empty-state-btn" onClick={() => navigate('/')}>
                  <i className="bi bi-search"></i>
                  <span>Explore Restaurants</span>
                  <i className="bi bi-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="reservations-list">
            {filteredReservations.map((reservation) => {
              const statusConfig = getStatusConfig(reservation.status);
              const paymentConfig = reservation.requiresPayment ? getPaymentConfig(reservation.paymentStatus) : null;
              const isPast = !isUpcomingReservation(reservation);

              return (
                <div key={reservation.id} className={`reservation-card ${isPast ? 'past' : ''}`}>
                  <div className="card-left">
                    <div className="date-box">
                      <span className="month">
                        {new Date(reservation.date).toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                      <span className="day">
                        {new Date(reservation.date).getDate()}
                      </span>
                      <span className="weekday">
                        {new Date(reservation.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>
                    </div>
                  </div>

                  <div className="card-main">
                    <div className="card-top">
                      <div className="restaurant-info">
                        <h3>{reservation.restaurant?.name || 'Restaurant'}</h3>
                        <span className="reservation-id">#{reservation.id}</span>
                      </div>
                      <div className="badges">
                        <span className={`status-badge ${statusConfig.class}`}>
                          <i className={`bi ${statusConfig.icon}`}></i>
                          {statusConfig.label}
                        </span>
                        {paymentConfig && (
                          <span className={`payment-badge ${paymentConfig.class}`}>
                            <i className={`bi ${paymentConfig.icon}`}></i>
                            {paymentConfig.label}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="card-details">
                      <div className="detail">
                        <i className="bi bi-clock"></i>
                        <span>{formatTime(reservation.time)}</span>
                      </div>
                      <div className="detail">
                        <i className="bi bi-people-fill"></i>
                        <span>{reservation.guests} {reservation.guests === 1 ? 'guest' : 'guests'}</span>
                      </div>
                      {reservation.restaurant?.location && (
                        <div className="detail">
                          <i className="bi bi-geo-alt"></i>
                          <span>{reservation.restaurant.location}</span>
                        </div>
                      )}
                    </div>

                    {reservation.comments && (
                      <div className="card-comments">
                        <i className="bi bi-chat-left-text"></i>
                        <span>{reservation.comments}</span>
                      </div>
                    )}

                    {/* Reservation Tags */}
                    {reservation.reservationTags && reservation.reservationTags.length > 0 && (
                      <div className="card-tags">
                        {reservation.reservationTags.map((rt) => (
                          <span
                            key={rt.id}
                            className="tag-badge"
                            style={{
                              backgroundColor: rt.tag?.color || '#6c757d',
                              color: getContrastColor(rt.tag?.color),
                            }}
                          >
                            {rt.tag?.name || 'Tag'}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="card-actions">
                      <button
                        className="btn-secondary"
                        onClick={() => navigate(`/restaurant/${reservation.restaurantId}`)}
                      >
                        <i className="bi bi-eye"></i>
                        <span>View</span>
                      </button>

                      {reservation.requiresPayment &&
                       ['pending', 'failed'].includes(reservation.paymentStatus?.toLowerCase()) &&
                       isUpcomingReservation(reservation) &&
                       reservation.status?.toLowerCase() !== 'cancelled' && (
                        <button
                          className="btn-success"
                          onClick={() => handleRetryPayment(reservation)}
                          disabled={paymentLoading === reservation.id}
                        >
                          {paymentLoading === reservation.id ? (
                            <>
                              <span className="spinner-sm"></span>
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <i className="bi bi-credit-card"></i>
                              <span>Pay Now</span>
                            </>
                          )}
                        </button>
                      )}

                      {canEditReservation(reservation) && (
                        <button
                          className="btn-outline"
                          onClick={() => handleEditReservation(reservation)}
                        >
                          <i className="bi bi-pencil"></i>
                          <span>Edit</span>
                        </button>
                      )}

                      {reservation.status?.toLowerCase() !== 'cancelled' &&
                       reservation.status?.toLowerCase() !== 'completed' &&
                       isUpcomingReservation(reservation) && (
                        <button
                          className="btn-danger"
                          onClick={() => handleCancelReservation(reservation.id)}
                        >
                          <i className="bi bi-x-lg"></i>
                          <span>Cancel</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editingReservation && (
        <EditReservationModal
          reservation={editingReservation}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
        />
      )}

      <Footer />
    </div>
  );
};

export default MyReservations;
